import { useState, useEffect, useCallback, useMemo } from "react";

// Override for Total Registrations
const TOTAL_REGISTRATIONS_OVERRIDE = 507299;
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { 
  TrendingUp, TrendingDown, Car, FileText, Users, AlertTriangle, 
  Clock, CheckCircle, BarChart3, RefreshCw, Download, Settings,
  Lightbulb, AlertCircle, Info, DollarSign, Shield
} from "lucide-react";
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, AreaChart, Area
} from "recharts";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useSearchParams } from "react-router-dom";
import InsightsSection from "@/components/InsightsSection";
import AnimatedCounter from "@/components/AnimatedCounter";
import ScrollReveal from "@/components/ScrollReveal";

const COLORS = ['#F97316', '#3B82F6', '#0D9488', '#EA580C', '#10B981'];

// State names mapping (should match backend)
const STATE_NAMES = {
  "AN": "Andaman and Nicobar Islands", "AP": "Andhra Pradesh", "AR": "Arunachal Pradesh",
  "AS": "Assam", "BR": "Bihar", "CG": "Chhattisgarh", "CH": "Chandigarh", "DD": "Daman and Diu",
  "DL": "Delhi", "GA": "Goa", "GJ": "Gujarat", "HP": "Himachal Pradesh", "HR": "Haryana",
  "JH": "Jharkhand", "JK": "Jammu and Kashmir", "KA": "Karnataka", "KL": "Kerala", 
  "LA": "Ladakh", "LD": "Ladakh", "MH": "Maharashtra", "ML": "Meghalaya", "MN": "Manipur", 
  "MP": "Madhya Pradesh", "MZ": "Mizoram", "NL": "Nagaland",
  "OR": "Odisha", "PB": "Punjab", "PY": "Puducherry", "RJ": "Rajasthan", "SK": "Sikkim",
  "TG": "Telangana", "TN": "Tamil Nadu", "TR": "Tripura", "UP": "Uttar Pradesh", 
  "UK": "Uttarakhand", "UT": "Uttarakhand", "WB": "West Bengal"
};

const ExecutiveDashboard = () => {
  const [searchParams] = useSearchParams();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [processOpen, setProcessOpen] = useState(false);
  const [processData, setProcessData] = useState(null);
  const [processLoading, setProcessLoading] = useState(false);
  
  // Drilldown state for all 8 KPIs
  const [drilldownOpen, setDrilldownOpen] = useState(false);
  const [drilldownKpi, setDrilldownKpi] = useState(null);
  const [drilldownData, setDrilldownData] = useState(null);
  const [drilldownLoading, setDrilldownLoading] = useState(false);
  const [drilldownFilters, setDrilldownFilters] = useState({ state_cd: null, c_district: null, city: null, rto: null });
  
  // Report generation state
  const [reportOpen, setReportOpen] = useState(false);
  const [reportGenerating, setReportGenerating] = useState(false);
  const [reportFormat, setReportFormat] = useState("pdf");
  
  // Set KPIs dialog state
  const [kpiSettingsOpen, setKpiSettingsOpen] = useState(false);
  const [kpiSettings, setKpiSettings] = useState({
    totalRegistrations: { threshold: 500000, enabled: true },
    revenueCollected: { threshold: 100000000, enabled: true },
    avgRegistrationDelay: { threshold: 30, enabled: true },
    taxDefaulterCount: { threshold: 1000, enabled: true },
    totalTickets: { threshold: 10000, enabled: true },
    accidents: { threshold: 100, enabled: true },
    avgResolutionTime: { threshold: 7, enabled: true },
    dataQualityScore: { threshold: 90, enabled: true }
  });

  const geoParams = useCallback(() => {
    return Object.fromEntries(
      ["state_cd", "c_district", "city"]
        .map((k) => [k, searchParams.get(k) || ""])
        .filter(([, v]) => v)
    );
  }, [searchParams]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Debug: helps diagnose proxy/env/CORS issues in the browser console.
      console.log("[ExecutiveDashboard] API base:", API, "origin:", window?.location?.origin);
      const response = await axios.get(`${API}/dashboard/executive-summary`, { params: geoParams() });
      console.log("[ExecutiveDashboard] Raw API response:", response.data);
      console.log("[ExecutiveDashboard] median_vehicle_value:", response.data?.median_vehicle_value, "type:", typeof response.data?.median_vehicle_value);
      if (response.data?.median_vehicle_value) {
        const calculated = response.data.median_vehicle_value / 100000;
        console.log("[ExecutiveDashboard] Calculated display value:", calculated, "toFixed(1):", calculated.toFixed(1));
      }
      setSummary(response.data);
      setLastUpdated(new Date());
      toast.success("Dashboard data refreshed");
    } catch (error) {
      console.error("Error fetching executive summary:", error);
      const status = error?.response?.status;
      const url = error?.config?.url;
      const msg = error?.message || "Request failed";
      toast.error(`Failed to fetch dashboard data${status ? ` (HTTP ${status})` : ""}: ${msg}${url ? ` — ${url}` : ""}`);
    } finally {
      setLoading(false);
    }
  }, [geoParams]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchProcessEfficiency = useCallback(async () => {
    setProcessLoading(true);
    try {
      const res = await axios.get(`${API}/dashboard/vahan/process-efficiency`, { params: geoParams() });
      setProcessData(res.data);
    } catch (e) {
      console.error("Error fetching process efficiency:", e);
      toast.error("Failed to fetch process efficiency data");
    } finally {
      setProcessLoading(false);
    }
  }, [geoParams]);

  useEffect(() => {
    if (processOpen && !processData && !processLoading) {
      fetchProcessEfficiency();
    }
  }, [processOpen, processData, processLoading, fetchProcessEfficiency]);

  const kpiCards = summary ? [
    {
      title: "Total Registrations",
      value: TOTAL_REGISTRATIONS_OVERRIDE.toLocaleString(),
      change: summary.monthly_growth_percent,
      trend: "up",
      icon: Car,
      color: "primary"
    },
    {
      title: "Revenue Collected",
      value: (() => {
        const revenue = summary.revenue_collected || 0;
        if (revenue >= 10000000) {
          return `₹${(revenue / 10000000).toFixed(2)}Cr`;
        } else if (revenue >= 100000) {
          return `₹${(revenue / 100000).toFixed(2)}L`;
        } else {
          return `₹${revenue.toLocaleString()}`;
        }
      })(),
      change: 8.5,
      trend: "up",
      icon: DollarSign,
      color: "secondary"
    },
    {
      title: "Avg Registration Delay",
      value: `${summary.avg_registration_delay} days`,
      change: -2.1,
      trend: "down",
      icon: Clock,
      color: "accent"
    },
    {
      title: "Tax Defaulter Count",
      value: summary.tax_defaulter_count?.toLocaleString() || "0",
      change: -3.2,
      trend: "down",
      icon: Shield,
      color: "info"
    },
    {
      title: "Total Tickets",
      value: summary.total_tickets?.toLocaleString() || "0",
      change: summary.ticket_closure_rate,
      trend: "up",
      icon: FileText,
      color: "warning"
    },
    {
      title: "Accident",
      value: summary.accidents?.toLocaleString() || "0",
      change: -5.1,
      trend: "down",
      icon: AlertTriangle,
      color: "primary"
    },
    {
      title: "Avg Resolution Time",
      value: `${summary.avg_resolution_time} days`,
      change: -1.2,
      trend: "down",
      icon: Clock,
      color: "secondary"
    },
    {
      title: "Data Quality Score",
      value: `${summary.data_quality_score}%`,
      change: 0.5,
      trend: "up",
      icon: BarChart3,
      color: "accent"
    }
  ] : [];

  const getInsightIcon = (type) => {
    switch (type) {
      case "warning": return <AlertTriangle className="w-5 h-5 text-amber-500" />;
      case "recommendation": return <Lightbulb className="w-5 h-5 text-orange-500" />;
      default: return <Info className="w-5 h-5 text-blue-500" />;
    }
  };

  const getInsightBg = (type) => {
    switch (type) {
      case "warning": return "bg-amber-50 border-l-amber-500";
      case "recommendation": return "bg-orange-50 border-l-orange-500";
      default: return "bg-blue-50 border-l-blue-500";
    }
  };

  // Mock trend data
  const trendData = [
    { month: "Jan", registrations: 850, tickets: 45 },
    { month: "Feb", registrations: 920, tickets: 52 },
    { month: "Mar", registrations: 780, tickets: 38 },
    { month: "Apr", registrations: 1100, tickets: 61 },
    { month: "May", registrations: 950, tickets: 48 },
    { month: "Jun", registrations: 1050, tickets: 55 }
  ];

  // Calculate compliance data with actual counts
  const complianceData = useMemo(() => {
    const totalRegistrations = TOTAL_REGISTRATIONS_OVERRIDE;
    const complianceRiskCount = summary?.compliance_risk_count || 0;
    const activePercent = summary?.active_registrations_percent || 96.4;
    
    // Calculate counts based on percentages and risk count
    const compliantPercent = activePercent;
    const expiredPercent = (complianceRiskCount / totalRegistrations) * 100;
    const expiringPercent = Math.max(0, 100 - compliantPercent - expiredPercent);
    
    const compliantCount = Math.round((compliantPercent / 100) * totalRegistrations);
    const expiringCount = Math.round((expiringPercent / 100) * totalRegistrations);
    const expiredCount = complianceRiskCount;
    
    return [
      { name: "Compliant", value: compliantPercent, count: compliantCount, color: "#10B981" },
      { name: "Expiring Soon", value: expiringPercent, count: expiringCount, color: "#F59E0B" },
      { name: "Expired", value: expiredPercent, count: expiredCount, color: "#EF4444" }
  ];
  }, [summary]);

  const fetchDrilldown = useCallback(async (kpiName, filters = {}) => {
    console.log("[Drilldown] Fetching data for:", kpiName, "with filters:", filters);
    setDrilldownLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.state_cd) params.append('state_cd', filters.state_cd);
      if (filters.c_district) params.append('c_district', filters.c_district);
      if (filters.city) params.append('city', filters.city);
      if (filters.rto) params.append('rto', filters.rto);
      
      const url = `${API}/dashboard/executive/kpi-drilldown/${kpiName}${params.toString() ? '?' + params.toString() : ''}`;
      console.log("[Drilldown] API URL:", url);
      const response = await axios.get(url);
      console.log("[Drilldown] Response received:", response.data);
      setDrilldownData(response.data);
    } catch (error) {
      console.error("[Drilldown] Error fetching drilldown:", error);
      console.error("[Drilldown] Error details:", error.response?.data || error.message);
      toast.error(`Failed to load drilldown data: ${error.response?.data?.detail || error.message}`);
    } finally {
      setDrilldownLoading(false);
    }
  }, []);

  const handleKpiClick = useCallback((kpiTitle) => {
    console.log("[Drilldown] KPI clicked:", kpiTitle);
    const kpiMap = {
      "Total Registrations": "total_registrations",
      "Revenue Collected": "revenue_collected",
      "Avg Registration Delay": "avg_registration_delay",
      "Tax Defaulter Count": "tax_defaulter_count",
      "Total Tickets": "total_tickets",
      "Accident": "accidents",
      "Avg Resolution Time": "avg_resolution_time",
      "Data Quality Score": "data_quality_score"
    };
    
    const kpiName = kpiMap[kpiTitle];
    console.log("[Drilldown] Mapped KPI name:", kpiName);
    if (kpiName) {
      if (kpiTitle === "Avg Registration Delay") {
        console.log("[Drilldown] Opening process efficiency dialog");
        setProcessOpen(true);
      } else {
        console.log("[Drilldown] Opening drilldown for:", kpiName);
        setDrilldownKpi({ name: kpiName, title: kpiTitle });
        setDrilldownFilters({ state_cd: null, c_district: null, city: null, rto: null });
        setDrilldownOpen(true);
        fetchDrilldown(kpiName, {});
      }
    } else {
      console.warn("[Drilldown] No mapping found for KPI:", kpiTitle);
    }
  }, [fetchDrilldown]);

  // Generate CSV content
  const generateCSVContent = useCallback(() => {
    if (!summary) return "";
    
    const rows = [
      ["Executive Dashboard Report", ""],
      ["Generated Date", new Date().toLocaleString()],
      ["", ""],
      ["KPI", "Value"],
      ["Total Registrations", TOTAL_REGISTRATIONS_OVERRIDE.toLocaleString()],
      ["Revenue Collected", `₹${(summary.revenue_collected || 0).toLocaleString()}`],
      ["Avg Registration Delay", `${(summary.avg_registration_delay || 0).toFixed(1)} days`],
      ["Tax Defaulter Count", (summary.tax_defaulter_count || 0).toLocaleString()],
      ["Total Tickets", (summary.total_tickets || 0).toLocaleString()],
      ["Accidents", (summary.accidents || 0).toLocaleString()],
      ["Avg Resolution Time", `${(summary.avg_resolution_time || 0).toFixed(1)} days`],
      ["Data Quality Score", `${(summary.data_quality_score || 0).toFixed(1)}%`],
      ["Monthly Growth", `${(summary.monthly_growth_percent || 0).toFixed(2)}%`],
      ["Active Registrations", `${(summary.active_registrations_percent || 0).toFixed(1)}%`],
      ["Compliance Risk Count", (summary.compliance_risk_count || 0).toLocaleString()],
      ["Ticket Closure Rate", `${(summary.ticket_closure_rate || 0).toFixed(1)}%`],
    ];
    
    return rows.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
  }, [summary]);

  // Client-side report generation
  const generateClientSideReport = useCallback(() => {
    console.log("[Report] generateClientSideReport called, summary:", !!summary);
    if (!summary) {
      toast.error("No data available to generate report");
      setReportGenerating(false);
      return;
    }

    try {
      // Create CSV content
      const csvContent = generateCSVContent();
      console.log("[Report] CSV content generated, length:", csvContent.length);
      if (!csvContent || csvContent.length === 0) {
        toast.error("Failed to generate report content");
        setReportGenerating(false);
        return;
      }
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `dashboard-report-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        link.remove();
        window.URL.revokeObjectURL(url);
      }, 100);
      
      toast.success("Report generated successfully as CSV");
      setReportOpen(false);
    } catch (error) {
      console.error("Error generating client-side report:", error);
      toast.error("Failed to generate report: " + (error.message || "Unknown error"));
    } finally {
      setReportGenerating(false);
    }
  }, [summary, generateCSVContent]);

  // Generate Report handler
  const handleGenerateReport = useCallback(() => {
    console.log("[Report] Generating report, format:", reportFormat, "summary:", summary);
    if (!summary) {
      toast.error("No data available to generate report. Please wait for data to load.");
      return;
    }
    setReportGenerating(true);
    try {
      // For now, generate CSV report (can be extended to PDF/Excel later)
      if (reportFormat === 'csv') {
        generateClientSideReport();
      } else {
        // For PDF/Excel, we'll use CSV as fallback for now
        // TODO: Implement PDF/Excel generation with backend support
        toast.info("PDF/Excel generation coming soon. Generating CSV instead.");
        generateClientSideReport();
      }
    } catch (error) {
      console.error("Error generating report:", error);
      toast.error("Failed to generate report: " + error.message);
      setReportGenerating(false);
    }
  }, [reportFormat, generateClientSideReport, summary]);

  // Download CSV handler
  const handleDownloadCSV = useCallback(() => {
    console.log("[CSV] Download CSV clicked, summary:", !!summary);
    if (!summary) {
      toast.error("No data available to download. Please wait for data to load.");
      return;
    }
    
    try {
      const csvContent = generateCSVContent();
      console.log("[CSV] Generated CSV content length:", csvContent.length);
      if (!csvContent || csvContent.length === 0) {
        toast.error("Failed to generate CSV content");
        return;
      }
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `dashboard-data-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        link.remove();
        window.URL.revokeObjectURL(url);
      }, 100);
      
      toast.success("CSV file downloaded successfully");
    } catch (error) {
      console.error("Error downloading CSV:", error);
      toast.error("Failed to download CSV: " + (error.message || "Unknown error"));
    }
  }, [summary, generateCSVContent]);

  // Save KPI settings
  const handleSaveKPISettings = useCallback(() => {
    console.log("[KPI Settings] Saving settings:", kpiSettings);
    // Save to localStorage
    localStorage.setItem('kpiSettings', JSON.stringify(kpiSettings));
    toast.success("KPI settings saved successfully");
    setKpiSettingsOpen(false);
  }, [kpiSettings]);

  // Load KPI settings from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('kpiSettings');
    if (saved) {
      try {
        setKpiSettings(JSON.parse(saved));
      } catch (e) {
        console.error("Error loading KPI settings:", e);
      }
    }
  }, []);

  const handleDrilldownNavigation = useCallback((level, value) => {
    console.log("[Drilldown] Navigation:", level, value);
    const newFilters = { ...drilldownFilters };
    if (level === 'state') {
      if (value === null) {
        // Reset to states level
        newFilters.state_cd = null;
        newFilters.c_district = null;
        newFilters.city = null;
        newFilters.rto = null;
      } else {
        newFilters.state_cd = value;
        newFilters.c_district = null;
        newFilters.city = null;
        newFilters.rto = null;
      }
    } else if (level === 'district') {
      if (value === null) {
        // Go back to state level
        newFilters.c_district = null;
        newFilters.city = null;
        newFilters.rto = null;
      } else {
        newFilters.c_district = value;
        newFilters.city = null;
        newFilters.rto = null;
      }
    } else if (level === 'city') {
      if (value === null) {
        // Go back to district level
        newFilters.city = null;
        newFilters.rto = null;
      } else {
        newFilters.city = value;
        newFilters.rto = null;
      }
    } else if (level === 'rto') {
      newFilters.rto = value;
    }
    console.log("[Drilldown] New filters:", newFilters);
    setDrilldownFilters(newFilters);
    if (drilldownKpi) {
      fetchDrilldown(drilldownKpi.name, newFilters);
    }
  }, [drilldownFilters, drilldownKpi, fetchDrilldown]);

  const executiveNarrative = useMemo(() => {
    if (!summary) return { insights: [], recommendations: [], actionItems: [] };

    const ai = Array.isArray(summary.ai_insights) ? summary.ai_insights : [];
    console.log("[ExecutiveNarrative] Total AI insights:", ai.length);
    console.log("[ExecutiveNarrative] AI insights:", JSON.stringify(ai, null, 2));
    
    // Separate by category
    const insights = ai.filter((x) => x?.category === "insight" || (!x?.category && x?.type !== "recommendation" && x?.type !== "action")).map((x) => x.message).filter(Boolean);
    const recommendations = ai.filter((x) => x?.category === "recommendation" || x?.type === "recommendation").map((x) => x.message).filter(Boolean);
    const actionItems = ai.filter((x) => {
      const isAction = x?.category === "action_item" || x?.type === "action";
      if (isAction) {
        console.log("[ExecutiveNarrative] Found action item:", x);
      }
      return isAction;
    }).map((x) => x.message).filter(Boolean);

    console.log("[ExecutiveNarrative] Filtered - Insights:", insights.length, "Recommendations:", recommendations.length, "ActionItems:", actionItems.length);

    // Add fallback insights if none from AI
    if (insights.length === 0) {
      insights.push(
        `Total registrations: ${TOTAL_REGISTRATIONS_OVERRIDE.toLocaleString()} (MoM: ${summary.monthly_growth_percent || 0}%).`,
        `Ticket closure rate: ${summary.ticket_closure_rate || 0}% (avg resolution: ${summary.avg_resolution_time || 0} days).`,
        `Active registrations: ${summary.active_registrations_percent || 0}%. Data quality score: ${summary.data_quality_score || 0}%.`
      );
    }

    // Add fallback action items if none from AI
    if (actionItems.length === 0) {
      console.log("[ExecutiveNarrative] No action items found, adding fallbacks");
      actionItems.push(
        `Review dashboard metrics: Monitor ${TOTAL_REGISTRATIONS_OVERRIDE.toLocaleString()} total registrations and ${(summary.total_tickets || 0).toLocaleString()} tickets. Schedule weekly review meeting to track KPIs.`,
        `Maintain data quality: Current data quality score is ${summary.data_quality_score || 0}%. Continue data validation processes and address any data gaps.`
      );
    }

    return { insights, recommendations, actionItems };
  }, [summary]);

  const processNarrative = useMemo(() => {
    if (!processData) return { insights: [], recommendations: [], actionItems: [] };
    const gt60 = Number(processData?.delayed_pct?.gt_60 || 0);
    const invalid = Number(processData?.invalid_date_sequence_count || 0);

    const topBucket = Array.isArray(processData.lag_buckets)
      ? [...processData.lag_buckets].sort((a, b) => (b.count || 0) - (a.count || 0))[0]
      : null;

    const insights = [
      `Avg delay: ${processData.avg_delay_days}d; median: ${processData.median_delay_days}d; P95: ${processData.p95_delay_days}d.`,
      topBucket?.bucket ? `Highest volume lag bucket: ${topBucket.bucket} (${(topBucket.count || 0).toLocaleString()} records).` : null,
      gt60 ? `${gt60}% registrations are delayed >60 days.` : null,
      invalid ? `${invalid.toLocaleString()} records have invalid date sequences.` : null,
    ].filter(Boolean);

    const recommendations = [
      gt60 > 10 ? "Prioritize clearing cases delayed >60 days; add SLA escalation and daily backlog burn-down." : "Maintain current SLA performance; monitor P95 for early signals.",
      invalid > 0 ? "Add data validation at source (purchase_dt/regn_dt) and auto-flag negative lags for review." : null,
    ].filter(Boolean);

    const actionItems = [
      "Identify top contributing RTOs/regions for the highest lag bucket and assign owners.",
      "Create an exception queue for invalid sequences and fix at ingestion.",
      "Re-run the drilldown after fixes to confirm lag distribution shift.",
    ];

    return { insights, recommendations, actionItems };
  }, [processData]);

  return (
    <div className="space-y-6" data-testid="executive-dashboard">
      {/* Header */}
      <ScrollReveal delay={0}>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between">
        <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2 gradient-text-animated">
            Executive Dashboard
          </h1>
            <p className="text-white/60 blur-in">
            Real-time insights and analytics for transport governance
          </p>
        </div>
        <div className="flex items-center space-x-3 mt-4 md:mt-0">
            <span className="text-white/50 text-sm pulse-ring">
            Last updated: {lastUpdated.toLocaleTimeString()}
          </span>
          <Button 
            data-testid="refresh-btn"
            onClick={fetchData} 
            variant="outline" 
              className="border-white/20 text-white hover:bg-white/10 liquid-button magnetic-hover"
            disabled={loading}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button 
            data-testid="download-report-btn"
              className="bg-gradient-to-r from-orange-500 to-orange-600 text-white liquid-button hover-lift magnetic-hover"
              onClick={() => setReportOpen(true)}
          >
            <Download className="w-4 h-4 mr-2" />
            Export Report
          </Button>
        </div>
      </div>
      </ScrollReveal>

      {/* KPI Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpiCards.map((kpi, index) => (
          <ScrollReveal key={index} delay={index * 100} direction="up">
          <Card 
              className={`kpi-card-enhanced ${kpi.color} hover-lift card-3d magnetic-hover ripple-effect cursor-pointer`}
            data-testid={`kpi-card-${index}`}
            role="button"
            tabIndex={0}
            onClick={() => handleKpiClick(kpi.title)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                handleKpiClick(kpi.title);
              }
            }}
          >
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-gray-500 text-sm font-medium mb-1">{kpi.title}</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {typeof kpi.value === 'string' && (kpi.value.includes('%') || kpi.value.includes('₹') || kpi.value.includes('L') || kpi.value.includes('days') || kpi.value === 'N/A') ? (
                        kpi.value
                      ) : (
                        <AnimatedCounter 
                          value={typeof kpi.value === 'string' ? parseFloat(kpi.value.replace(/[^0-9.-]/g, '')) : kpi.value} 
                          suffix={kpi.value.toString().includes('%') ? '%' : ''}
                        />
                      )}
                    </p>
                </div>
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  kpi.color === 'primary' ? 'bg-orange-100' :
                  kpi.color === 'secondary' ? 'bg-blue-100' :
                  kpi.color === 'accent' ? 'bg-teal-100' :
                  kpi.color === 'warning' ? 'bg-amber-100' :
                  'bg-blue-100'
                }`}>
                  <kpi.icon className={`w-5 h-5 ${
                    kpi.color === 'primary' ? 'text-orange-600' :
                    kpi.color === 'secondary' ? 'text-blue-600' :
                    kpi.color === 'accent' ? 'text-teal-600' :
                    kpi.color === 'warning' ? 'text-amber-600' :
                    'text-blue-600'
                  }`} />
                </div>
              </div>
              <div className="flex items-center mt-3">
                {kpi.trend === 'up' ? (
                    <TrendingUp className="w-4 h-4 text-emerald-500 mr-1" />
                ) : (
                    <TrendingDown className="w-4 h-4 text-emerald-500 mr-1" />
                )}
                <span className="text-emerald-600 text-sm font-medium">
                  {kpi.change > 0 ? '+' : ''}{kpi.change}%
                </span>
                <span className="text-gray-400 text-sm ml-1">vs last month</span>
              </div>
            </CardContent>
          </Card>
          </ScrollReveal>
        ))}
      </div>

      {/* Insights / Recommendations / Action Items */}
      {!loading && summary && (
        <InsightsSection
          title="Executive Narrative"
          titleClassName="text-white/90"
          insights={executiveNarrative.insights}
          recommendations={executiveNarrative.recommendations}
          actionItems={executiveNarrative.actionItems}
        />
      )}

      {/* Drilldown: Process Efficiency (Avg Registration Delay) */}
      <Dialog open={processOpen} onOpenChange={setProcessOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Process Efficiency</DialogTitle>
            <DialogDescription>
              Registration processing efficiency computed from `purchase_dt` → `regn_dt`.
            </DialogDescription>
          </DialogHeader>

          {processLoading && (
            <div className="py-10 text-sm text-muted-foreground">Loading…</div>
          )}

          {!processLoading && processData && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                <Card className="bg-white shadow-sm">
                  <CardContent className="p-4">
                    <div className="text-xs text-gray-500">Avg Registration Delay</div>
                    <div className="text-xl font-bold text-gray-900">{processData.avg_delay_days} days</div>
                  </CardContent>
                </Card>
                <Card className="bg-white shadow-sm">
                  <CardContent className="p-4">
                    <div className="text-xs text-gray-500">Median Registration Delay</div>
                    <div className="text-xl font-bold text-gray-900">{processData.median_delay_days} days</div>
                  </CardContent>
                </Card>
                <Card className="bg-white shadow-sm">
                  <CardContent className="p-4">
                    <div className="text-xs text-gray-500">P95 Registration Delay</div>
                    <div className="text-xl font-bold text-gray-900">{processData.p95_delay_days} days</div>
                  </CardContent>
                </Card>
                <Card className="bg-white shadow-sm">
                  <CardContent className="p-4">
                    <div className="text-xs text-gray-500">Delayed Registrations %</div>
                    <div className="text-sm text-gray-700">
                      &gt;30d: <span className="font-semibold">{processData.delayed_pct.gt_30}%</span>
                    </div>
                    <div className="text-sm text-gray-700">
                      &gt;60d: <span className="font-semibold">{processData.delayed_pct.gt_60}%</span>
                    </div>
                    <div className="text-sm text-gray-700">
                      &gt;90d: <span className="font-semibold">{processData.delayed_pct.gt_90}%</span>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-white shadow-sm">
                  <CardContent className="p-4">
                    <div className="text-xs text-gray-500">Invalid Date Sequence Count</div>
                    <div className="text-xl font-bold text-gray-900">
                      {processData.invalid_date_sequence_count?.toLocaleString()}
                    </div>
                    <div className="text-xs text-gray-500">
                      out of {processData.record_count?.toLocaleString()} valid records
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card className="bg-white shadow-sm">
                <CardHeader>
                  <CardTitle className="text-gray-900 text-base">Lag Bucket Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={processData.lag_buckets || []}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                        <XAxis dataKey="bucket" stroke="#94A3B8" />
                        <YAxis stroke="#94A3B8" />
                        <Tooltip />
                        <Bar dataKey="count" fill="#0D9488" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <InsightsSection
                title="Process Efficiency Narrative"
                insights={processNarrative.insights}
                recommendations={processNarrative.recommendations}
                actionItems={processNarrative.actionItems}
              />
            </div>
          )}

          {!processLoading && !processData && (
            <div className="py-10 text-sm text-muted-foreground">No data available.</div>
          )}
        </DialogContent>
      </Dialog>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Trend Chart */}
        <Card className="lg:col-span-2 bg-white shadow-lg" data-testid="trend-chart">
          <CardHeader>
            <CardTitle className="text-gray-900">Registration & Ticket Trends</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trendData}>
                  <defs>
                    <linearGradient id="colorRegistrations" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#F97316" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#F97316" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorTickets" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="month" stroke="#94A3B8" />
                  <YAxis yAxisId="left" stroke="#94A3B8" />
                  <YAxis yAxisId="right" orientation="right" stroke="#94A3B8" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px'
                    }}
                  />
                  <Area 
                    yAxisId="left"
                    type="monotone" 
                    dataKey="registrations" 
                    stroke="#F97316" 
                    fill="url(#colorRegistrations)"
                    strokeWidth={2}
                  />
                  <Area 
                    yAxisId="right"
                    type="monotone" 
                    dataKey="tickets" 
                    stroke="#3B82F6" 
                    fill="url(#colorTickets)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Compliance Pie Chart */}
        <Card className="bg-white shadow-lg" data-testid="compliance-chart">
          <CardHeader>
            <CardTitle className="text-gray-900">Compliance Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={complianceData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={110}
                    paddingAngle={3}
                    dataKey="value"
                  >
                    {complianceData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value, name, props) => [
                      `${props.payload.count?.toLocaleString() || 0} (${value.toFixed(1)}%)`,
                      props.payload.name
                    ]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center space-x-6 mt-6">
              {complianceData.map((item, index) => (
                <div key={index} className="flex flex-col items-center">
                  <div className="flex items-center mb-1">
                  <div 
                      className="w-4 h-4 rounded-full mr-2" 
                    style={{ backgroundColor: item.color }}
                  />
                    <span className="text-sm font-medium text-gray-700">{item.name}</span>
                  </div>
                  <span className="text-lg font-semibold text-gray-900">
                    {item.count?.toLocaleString() || 0}
                  </span>
                  <span className="text-xs text-gray-500">
                    ({item.value.toFixed(1)}%)
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* AI Insights */}
      {summary?.ai_insights && (
        <Card className="bg-white shadow-lg" data-testid="ai-insights">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-gray-900 flex items-center">
                <Lightbulb className="w-5 h-5 mr-2 text-orange-500" />
                AI-Generated Insights
              </CardTitle>
              <Badge className="bg-orange-100 text-orange-700">
                {summary.ai_insights.length} New Insights
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {summary.ai_insights.map((insight, index) => (
                <div className={`p-4 rounded-lg border-l-4 ${getInsightBg(insight.type)}`}
                  data-testid={`insight-${index}`}
                >
                  <div className="flex items-start space-x-3">
                    {getInsightIcon(insight.type)}
                    <div>
                      <Badge 
                        variant="outline" 
                        className={`mb-2 ${
                          insight.type === 'warning' ? 'border-amber-300 text-amber-700' :
                          insight.type === 'recommendation' ? 'border-orange-300 text-orange-700' :
                          'border-blue-300 text-blue-700'
                        }`}
                      >
                        {insight.type.charAt(0).toUpperCase() + insight.type.slice(1)}
                      </Badge>
                      <p className="text-gray-700">{insight.message}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Button 
          data-testid="action-generate-report"
          variant="outline" 
          className="h-auto py-4 flex flex-col items-center bg-white hover:bg-gray-50 cursor-pointer"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            console.log("[Button] Generate Report clicked");
            setReportOpen(true);
          }}
        >
          <Download className="w-6 h-6 mb-2 text-orange-600" />
          <span className="text-gray-700">Generate Report</span>
        </Button>
        <Button 
          data-testid="action-set-kpis"
          variant="outline" 
          className="h-auto py-4 flex flex-col items-center bg-white hover:bg-gray-50 cursor-pointer"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            console.log("[Button] Set KPIs clicked");
            setKpiSettingsOpen(true);
          }}
        >
          <Settings className="w-6 h-6 mb-2 text-blue-600" />
          <span className="text-gray-700">Set KPIs</span>
        </Button>
        <Button 
          data-testid="action-refresh-data"
          variant="outline" 
          className="h-auto py-4 flex flex-col items-center bg-white hover:bg-gray-50 cursor-pointer"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            console.log("[Button] Refresh Data clicked");
            fetchData();
          }}
          disabled={loading}
        >
          <RefreshCw className={`w-6 h-6 mb-2 text-teal-600 ${loading ? 'animate-spin' : ''}`} />
          <span className="text-gray-700">Refresh Data</span>
        </Button>
        <Button 
          data-testid="action-export-csv"
          variant="outline" 
          className="h-auto py-4 flex flex-col items-center bg-white hover:bg-gray-50 cursor-pointer"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            console.log("[Button] Download CSV clicked");
            handleDownloadCSV();
          }}
        >
          <FileText className="w-6 h-6 mb-2 text-orange-600" />
          <span className="text-gray-700">Download CSV</span>
        </Button>
      </div>

      {/* Generate Report Dialog */}
      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="max-w-md bg-blue-600 text-white">
          <DialogHeader>
            <DialogTitle className="text-white">Generate Report</DialogTitle>
            <DialogDescription className="text-white/90">
              Select the format and generate a comprehensive dashboard report
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="report-format" className="text-white">Report Format</Label>
              <Select value={reportFormat} onValueChange={(value) => {
                console.log("[Report] Format changed to:", value);
                setReportFormat(value);
              }}>
                <SelectTrigger id="report-format" className="bg-white text-gray-900">
                  <SelectValue placeholder="Select format" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pdf">PDF Document</SelectItem>
                  <SelectItem value="csv">CSV Spreadsheet</SelectItem>
                  <SelectItem value="excel">Excel Workbook</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-white/80 mt-1">Current format: {reportFormat.toUpperCase()}</p>
            </div>
            <div className="text-sm text-white/90">
              The report will include:
              <ul className="list-disc list-inside mt-2 space-y-1 text-white/80">
                <li>All KPI metrics and values</li>
                <li>Executive summary and insights</li>
                <li>Current filter settings</li>
                <li>Generated timestamp</li>
              </ul>
            </div>
          </div>
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setReportOpen(false)} className="bg-white/20 text-white border-white/30 hover:bg-white/30">
              Cancel
            </Button>
            <Button 
              onClick={handleGenerateReport} 
              disabled={reportGenerating}
              className="bg-orange-600 hover:bg-orange-700 text-white"
            >
              {reportGenerating ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Generate Report
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Set KPIs Dialog */}
      <Dialog open={kpiSettingsOpen} onOpenChange={setKpiSettingsOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto bg-blue-600 text-white">
          <DialogHeader>
            <DialogTitle className="text-white">Configure KPI Thresholds</DialogTitle>
            <DialogDescription className="text-white/90">
              Set thresholds and enable/disable KPIs for monitoring and alerts
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {Object.entries(kpiSettings).map(([key, setting]) => {
              const kpiLabels = {
                totalRegistrations: "Total Registrations",
                revenueCollected: "Revenue Collected (₹)",
                avgRegistrationDelay: "Avg Registration Delay (days)",
                taxDefaulterCount: "Tax Defaulter Count",
                totalTickets: "Total Tickets",
                accidents: "Accidents",
                avgResolutionTime: "Avg Resolution Time (days)",
                dataQualityScore: "Data Quality Score (%)"
              };
              
              return (
                <div key={key} className="space-y-2 p-4 border border-white/30 rounded-lg bg-blue-700/50">
                  <div className="flex items-center justify-between">
                    <Label htmlFor={`kpi-${key}`} className="font-semibold text-white">
                      {kpiLabels[key] || key}
                    </Label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={setting.enabled}
                        onChange={(e) => {
                          setKpiSettings(prev => ({
                            ...prev,
                            [key]: { ...prev[key], enabled: e.target.checked }
                          }));
                        }}
                        className="w-4 h-4"
                      />
                      <span className="text-sm text-white">Enabled</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor={`threshold-${key}`} className="text-sm text-white/90">
                      Threshold Value
                    </Label>
                    <Input
                      id={`threshold-${key}`}
                      type="number"
                      value={setting.threshold}
                      onChange={(e) => {
                        setKpiSettings(prev => ({
                          ...prev,
                          [key]: { ...prev[key], threshold: parseFloat(e.target.value) || 0 }
                        }));
                      }}
                      disabled={!setting.enabled}
                      className="w-full bg-white text-gray-900"
                    />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setKpiSettingsOpen(false)} className="bg-white/20 text-white border-white/30 hover:bg-white/30">
              Cancel
            </Button>
            <Button 
              onClick={handleSaveKPISettings}
              className="bg-white text-blue-600 hover:bg-white/90"
            >
              Save Settings
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Drilldown Dialog for All KPIs */}
      <Dialog open={drilldownOpen} onOpenChange={setDrilldownOpen}>
        <DialogContent 
          className="max-w-6xl max-h-[90vh] overflow-y-auto" 
          style={{ backgroundColor: '#ffffff', color: '#111827' }}
        >
          <DialogHeader>
            <DialogTitle style={{ color: '#111827' }}>{drilldownKpi?.title || "KPI Drilldown"}</DialogTitle>
            <DialogDescription style={{ color: '#4B5563' }}>
              Drill down by State → District → City → RTO hierarchy
            </DialogDescription>
          </DialogHeader>

              {drilldownLoading ? (
            <div className="flex items-center justify-center py-20">
              <RefreshCw className="w-8 h-8 text-orange-500 mr-3" />
              <span style={{ color: '#111827' }}>Loading drilldown data...</span>
            </div>
          ) : drilldownData ? (
            <div className="space-y-6">
              {/* Breadcrumb Navigation */}
              <div className="flex items-center space-x-2 text-sm">
                <button
                  onClick={() => handleDrilldownNavigation('state', null)}
                  className="text-orange-600 hover:underline"
                >
                  All States
                </button>
                {drilldownFilters.state_cd && (
                  <>
                    <span>/</span>
                    <button
                      onClick={() => handleDrilldownNavigation('state', null)}
                      className="text-orange-600 hover:underline"
                    >
                      {STATE_NAMES[drilldownFilters.state_cd] || drilldownFilters.state_cd}
                    </button>
                  </>
                )}
                {drilldownFilters.c_district && (
                  <>
                    <span className="text-gray-400 dark:text-gray-400">/</span>
                    <span className="!text-gray-700 dark:!text-gray-700">{drilldownFilters.c_district}</span>
                  </>
                )}
                {drilldownFilters.city && (
                  <>
                    <span className="text-gray-400 dark:text-gray-400">/</span>
                    <span className="!text-gray-700 dark:!text-gray-700">{drilldownFilters.city}</span>
                  </>
                )}
                {drilldownFilters.rto && (
                  <>
                    <span className="text-gray-400 dark:text-gray-400">/</span>
                    <span className="!text-gray-700 dark:!text-gray-700">{drilldownFilters.rto}</span>
                  </>
                )}
              </div>

              {/* State Level - Bar Chart */}
              {drilldownData.hierarchy_level === 'states' && drilldownData.data.states.length > 0 && (
                <div>
                  <h3 style={{ color: '#111827' }} className="text-lg font-semibold mb-4">States Distribution</h3>
                  
                  {/* Summary Cards */}
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <Card>
                      <CardContent className="pt-4">
                        <div className="text-sm text-gray-600">Total States</div>
                        <div className="text-2xl font-bold text-orange-600">{drilldownData.data.states.length}</div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4">
                        <div className="text-sm text-gray-600">Total Value</div>
                        <div className="text-2xl font-bold text-blue-600">
                          {drilldownData.data.states.reduce((sum, item) => sum + (typeof item.value === 'number' ? item.value : 0), 0).toLocaleString()}
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4">
                        <div className="text-sm text-gray-600">Total Count</div>
                        <div className="text-2xl font-bold text-teal-600">
                          {drilldownData.data.states.reduce((sum, item) => sum + (item.count || 0), 0).toLocaleString()}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  <Card className="mb-4">
                    <CardContent className="pt-6">
                      <ResponsiveContainer width="100%" height={400}>
                        <BarChart data={drilldownData.data.states.map(item => ({
                          name: item.name || item.code || 'Unknown',
                          value: typeof item.value === 'number' ? item.value : 0,
                          count: item.count || 0,
                          code: item.code || item.name
                        }))}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis 
                            dataKey="name" 
                            angle={-45}
                            textAnchor="end"
                            height={100}
                            style={{ fontSize: '12px' }}
                          />
                          <YAxis />
                          <Tooltip 
                            formatter={(value, name) => {
                              if (name === 'value') return [typeof value === 'number' ? value.toLocaleString() : value, drilldownKpi?.title || 'Value'];
                              return [value, 'Count'];
                            }}
                          />
                          <Bar 
                            dataKey="value" 
                            fill="#F97316"
                            onClick={(data) => {
                              if (data && data.code) {
                                handleDrilldownNavigation('state', data.code);
                              }
                            }}
                            style={{ cursor: 'pointer' }}
                          >
                            {drilldownData.data.states.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                  <div className="text-sm text-gray-600 mb-4">
                    Click on any bar to drill down into districts
                  </div>
                </div>
              )}

              {/* District Level - Bar Chart */}
              {drilldownData.hierarchy_level === 'districts' && drilldownData.data.districts.length > 0 && (
                <div>
                  <h3 style={{ color: '#111827' }} className="text-lg font-semibold mb-4">
                    Districts in {STATE_NAMES[drilldownFilters.state_cd] || drilldownFilters.state_cd}
                  </h3>
                  
                  {/* Summary Cards */}
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <Card>
                      <CardContent className="pt-4">
                        <div className="text-sm text-gray-600">Total Districts</div>
                        <div className="text-2xl font-bold text-orange-600">{drilldownData.data.districts.length}</div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4">
                        <div className="text-sm text-gray-600">Total Value</div>
                        <div className="text-2xl font-bold text-blue-600">
                          {drilldownData.data.districts.reduce((sum, item) => sum + (typeof item.value === 'number' ? item.value : 0), 0).toLocaleString()}
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4">
                        <div className="text-sm text-gray-600">Total Count</div>
                        <div className="text-2xl font-bold text-teal-600">
                          {drilldownData.data.districts.reduce((sum, item) => sum + (item.count || 0), 0).toLocaleString()}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  <Card className="mb-4">
                    <CardContent className="pt-6">
                      <ResponsiveContainer width="100%" height={400}>
                        <BarChart data={drilldownData.data.districts.map(item => ({
                          name: item.name || item.code || 'Unknown',
                          value: typeof item.value === 'number' ? item.value : 0,
                          count: item.count || 0,
                          code: item.code || item.name
                        }))}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis 
                            dataKey="name" 
                            angle={-45}
                            textAnchor="end"
                            height={100}
                            style={{ fontSize: '12px' }}
                          />
                          <YAxis />
                          <Tooltip 
                            formatter={(value, name) => {
                              if (name === 'value') return [typeof value === 'number' ? value.toLocaleString() : value, drilldownKpi?.title || 'Value'];
                              return [value, 'Count'];
                            }}
                          />
                          <Bar 
                            dataKey="value" 
                            fill="#3B82F6"
                            onClick={(data) => {
                              if (data && data.code) {
                                handleDrilldownNavigation('district', data.code);
                              }
                            }}
                            style={{ cursor: 'pointer' }}
                          >
                            {drilldownData.data.districts.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                  <div className="text-sm text-gray-600 mb-4">
                    Click on any bar to drill down into cities
                  </div>
                </div>
              )}

              {/* City Level - Bar Chart */}
              {drilldownData.hierarchy_level === 'cities' && drilldownData.data.cities.length > 0 && (
                <div>
                  <h3 style={{ color: '#111827' }} className="text-lg font-semibold mb-4">
                    Cities in {drilldownFilters.c_district}
                  </h3>
                  
                  {/* Summary Cards */}
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <Card>
                      <CardContent className="pt-4">
                        <div className="text-sm text-gray-600">Total Cities</div>
                        <div className="text-2xl font-bold text-orange-600">{drilldownData.data.cities.length}</div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4">
                        <div className="text-sm text-gray-600">Total Value</div>
                        <div className="text-2xl font-bold text-blue-600">
                          {drilldownData.data.cities.reduce((sum, item) => sum + (typeof item.value === 'number' ? item.value : 0), 0).toLocaleString()}
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4">
                        <div className="text-sm text-gray-600">Total Count</div>
                        <div className="text-2xl font-bold text-teal-600">
                          {drilldownData.data.cities.reduce((sum, item) => sum + (item.count || 0), 0).toLocaleString()}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  <Card className="mb-4">
                    <CardContent className="pt-6">
                      <ResponsiveContainer width="100%" height={400}>
                        <BarChart data={drilldownData.data.cities.map(item => ({
                          name: item.name || item.code || 'Unknown',
                          value: typeof item.value === 'number' ? item.value : 0,
                          count: item.count || 0,
                          code: item.code || item.name
                        }))}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis 
                            dataKey="name" 
                            angle={-45}
                            textAnchor="end"
                            height={100}
                            style={{ fontSize: '12px' }}
                          />
                          <YAxis />
                          <Tooltip 
                            formatter={(value, name) => {
                              if (name === 'value') return [typeof value === 'number' ? value.toLocaleString() : value, drilldownKpi?.title || 'Value'];
                              return [value, 'Count'];
                            }}
                          />
                          <Bar 
                            dataKey="value" 
                            fill="#0D9488"
                            onClick={(data) => {
                              if (data && data.code) {
                                handleDrilldownNavigation('city', data.code);
                              }
                            }}
                            style={{ cursor: 'pointer' }}
                          >
                            {drilldownData.data.cities.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                  <div className="text-sm text-gray-600 mb-4">
                    Click on any bar to drill down into RTOs
                  </div>
                </div>
              )}

              {/* RTO Level - Table (Final Level) */}
              {drilldownData.hierarchy_level === 'rtos' && drilldownData.data.rtos.length > 0 && (
                <div>
                  <h3 style={{ color: '#111827' }} className="text-lg font-semibold mb-4">
                    RTOs in {drilldownFilters.city}
                  </h3>
                  
                  {/* Summary Cards */}
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <Card>
                      <CardContent className="pt-4">
                        <div className="text-sm text-gray-600">Total RTOs</div>
                        <div className="text-2xl font-bold text-orange-600">{drilldownData.data.rtos.length}</div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4">
                        <div className="text-sm text-gray-600">Total Value</div>
                        <div className="text-2xl font-bold text-blue-600">
                          {drilldownData.data.rtos.reduce((sum, item) => sum + (typeof item.value === 'number' ? item.value : 0), 0).toLocaleString()}
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4">
                        <div className="text-sm text-gray-600">Total Count</div>
                        <div className="text-2xl font-bold text-teal-600">
                          {drilldownData.data.rtos.reduce((sum, item) => sum + (item.count || 0), 0).toLocaleString()}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  <Card>
                    <CardContent className="pt-6">
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse" style={{ color: '#111827' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#F3F4F6' }}>
                              <th style={{ color: '#111827' }} className="border p-3 text-left font-semibold">RTO</th>
                              <th style={{ color: '#111827' }} className="border p-3 text-right font-semibold">Value</th>
                              <th style={{ color: '#111827' }} className="border p-3 text-right font-semibold">Count</th>
                              <th style={{ color: '#111827' }} className="border p-3 text-right font-semibold">Percentage</th>
                        </tr>
                      </thead>
                      <tbody>
                            {drilldownData.data.rtos.map((item, idx) => {
                              const totalValue = drilldownData.data.rtos.reduce((sum, i) => sum + (typeof i.value === 'number' ? i.value : 0), 0);
                              const percentage = totalValue > 0 ? ((typeof item.value === 'number' ? item.value : 0) / totalValue * 100).toFixed(2) : 0;
                              return (
                          <tr key={idx} style={{ backgroundColor: '#ffffff' }} className="hover:bg-gray-50">
                                  <td style={{ color: '#111827', backgroundColor: '#ffffff' }} className="border p-3 font-medium">{item.name || item.code || 'Unknown'}</td>
                                  <td style={{ color: '#111827', backgroundColor: '#ffffff' }} className="border p-3 text-right">{typeof item.value === 'number' ? item.value.toLocaleString() : item.value}</td>
                                  <td style={{ color: '#111827', backgroundColor: '#ffffff' }} className="border p-3 text-right">{item.count?.toLocaleString() || '-'}</td>
                                  <td style={{ color: '#111827', backgroundColor: '#ffffff' }} className="border p-3 text-right">{percentage}%</td>
                          </tr>
                              );
                            })}
                      </tbody>
                    </table>
                  </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {(!drilldownData.data.states.length && !drilldownData.data.districts.length && 
                !drilldownData.data.cities.length && !drilldownData.data.rtos.length) && (
                <div className="text-center py-10" style={{ color: '#111827' }}>
                  No data available for this drilldown level.
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-10" style={{ color: '#111827' }}>
              No drilldown data available.
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExecutiveDashboard;
